import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShoppingCart,
  Filter,
  Star,
  Plus,
  Check,
  Shirt,
  X,
  Minus,
} from 'lucide-react';
import { useCartStore } from '../../store/cart';
import { formatPrice } from '../../lib/utils';

// Unsplash placeholder images
const PRODUCT_IMAGES = {
  tshirt1: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=400&q=80',
  tshirt2: 'https://images.unsplash.com/photo-1583743814966-8936f5b7be1a?w=400&q=80',
  tshirt3: 'https://images.unsplash.com/photo-1576566588028-4147f3842f27?w=400&q=80',
  cap: 'https://images.unsplash.com/photo-1588850561407-ed78c282e89b?w=400&q=80',
  hoodie: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=400&q=80',
  jacket: 'https://images.unsplash.com/photo-1551028719-00167b16eac5?w=400&q=80',
  mug: 'https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?w=400&q=80',
  sticker: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=80',
  towel: 'https://images.unsplash.com/photo-1583845112239-97ef1341b271?w=400&q=80',
  flag: 'https://images.unsplash.com/photo-1569517282132-25d22f4573e6?w=400&q=80',
  bottle: 'https://images.unsplash.com/photo-1602143407151-7111542de6e8?w=400&q=80',
  keychain: 'https://images.unsplash.com/photo-1584735422195-a1c1d7a40a2c?w=400&q=80',
  poster: 'https://images.unsplash.com/photo-1586075010923-2dd4570fb338?w=400&q=80',
  lanyard: 'https://images.unsplash.com/photo-1631729371254-42c2892f0e6e?w=400&q=80',
};

interface Product {
  id: string;
  name: string;
  slug: string;
  price: number;
  category: string;
  subcategory?: string;
  image: string;
  description: string;
  featured: boolean;
  inStock: boolean;
  rating: number;
  reviews: number;
  sizes?: string[];
  colors?: { name: string; hex: string }[];
}

const products: Product[] = [
  // T-Shirts
  {
    id: 't1',
    name: 'DriveIT NQ Racing Tee',
    slug: 'racing-tee',
    price: 4500,
    category: 'Apparel',
    subcategory: 'T-Shirts',
    image: PRODUCT_IMAGES.tshirt1,
    description: 'Premium cotton racing t-shirt with DriveIT NQ logo',
    featured: true,
    inStock: true,
    rating: 4.8,
    reviews: 24,
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    colors: [
      { name: 'Black', hex: '#1a1a1a' },
      { name: 'White', hex: '#ffffff' },
      { name: 'Red', hex: '#dc2626' },
    ],
  },
  {
    id: 't2',
    name: 'Circuit Map T-Shirt',
    slug: 'circuit-map-tee',
    price: 4900,
    category: 'Apparel',
    subcategory: 'T-Shirts',
    image: PRODUCT_IMAGES.tshirt2,
    description: 'Featuring the iconic 2.75km main circuit layout',
    featured: true,
    inStock: true,
    rating: 4.9,
    reviews: 18,
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    colors: [
      { name: 'Navy', hex: '#1e3a5f' },
      { name: 'Black', hex: '#1a1a1a' },
    ],
  },
  {
    id: 't3',
    name: 'Vintage Racing Tee',
    slug: 'vintage-tee',
    price: 3900,
    category: 'Apparel',
    subcategory: 'T-Shirts',
    image: PRODUCT_IMAGES.tshirt3,
    description: 'Retro-style racing graphic t-shirt',
    featured: false,
    inStock: true,
    rating: 4.5,
    reviews: 12,
    sizes: ['S', 'M', 'L', 'XL'],
  },
  // Headwear
  {
    id: 'h1',
    name: 'DriveIT NQ Cap',
    slug: 'driveit-cap',
    price: 3500,
    category: 'Headwear',
    image: PRODUCT_IMAGES.cap,
    description: 'Adjustable snapback cap with embroidered logo',
    featured: true,
    inStock: true,
    rating: 4.7,
    reviews: 31,
    colors: [
      { name: 'Black', hex: '#1a1a1a' },
      { name: 'Red', hex: '#dc2626' },
      { name: 'Navy', hex: '#1e3a5f' },
    ],
  },
  {
    id: 'h2',
    name: 'Racing Bucket Hat',
    slug: 'bucket-hat',
    price: 3200,
    category: 'Headwear',
    image: PRODUCT_IMAGES.cap,
    description: 'Sun protection with racing style',
    featured: false,
    inStock: true,
    rating: 4.4,
    reviews: 8,
    sizes: ['S/M', 'L/XL'],
  },
  // Outerwear
  {
    id: 'o1',
    name: 'Team Hoodie',
    slug: 'team-hoodie',
    price: 8500,
    category: 'Apparel',
    subcategory: 'Outerwear',
    image: PRODUCT_IMAGES.hoodie,
    description: 'Warm fleece hoodie with racing graphics',
    featured: true,
    inStock: true,
    rating: 4.9,
    reviews: 15,
    sizes: ['S', 'M', 'L', 'XL', '2XL'],
    colors: [
      { name: 'Black', hex: '#1a1a1a' },
      { name: 'Grey', hex: '#6b7280' },
    ],
  },
  {
    id: 'o2',
    name: 'Racing Jacket',
    slug: 'racing-jacket',
    price: 14500,
    category: 'Apparel',
    subcategory: 'Outerwear',
    image: PRODUCT_IMAGES.jacket,
    description: 'Lightweight windbreaker with sponsor logos',
    featured: false,
    inStock: false,
    rating: 4.6,
    reviews: 8,
    sizes: ['S', 'M', 'L', 'XL'],
  },
  // Towels
  {
    id: 'tw1',
    name: 'Paddock Towel',
    slug: 'paddock-towel',
    price: 2800,
    category: 'Towels',
    image: PRODUCT_IMAGES.towel,
    description: 'Large cotton towel with DriveIT NQ branding',
    featured: false,
    inStock: true,
    rating: 4.6,
    reviews: 19,
    colors: [
      { name: 'Red', hex: '#dc2626' },
      { name: 'Black', hex: '#1a1a1a' },
    ],
  },
  {
    id: 'tw2',
    name: 'Cooling Towel',
    slug: 'cooling-towel',
    price: 2200,
    category: 'Towels',
    image: PRODUCT_IMAGES.towel,
    description: 'Stay cool trackside with our sports cooling towel',
    featured: false,
    inStock: true,
    rating: 4.8,
    reviews: 27,
  },
  // Flags
  {
    id: 'f1',
    name: 'Supporter Flag',
    slug: 'supporter-flag',
    price: 3500,
    category: 'Flags',
    image: PRODUCT_IMAGES.flag,
    description: 'Large 150x90cm flag - show your support!',
    featured: false,
    inStock: true,
    rating: 4.7,
    reviews: 14,
  },
  {
    id: 'f2',
    name: 'Car Window Flag',
    slug: 'window-flag',
    price: 1500,
    category: 'Flags',
    image: PRODUCT_IMAGES.flag,
    description: 'Clips to your car window - set of 2',
    featured: false,
    inStock: true,
    rating: 4.3,
    reviews: 22,
  },
  // Drinkware
  {
    id: 'd1',
    name: 'Paddock Mug',
    slug: 'paddock-mug',
    price: 2500,
    category: 'Drinkware',
    image: PRODUCT_IMAGES.mug,
    description: 'Ceramic coffee mug with track map',
    featured: false,
    inStock: true,
    rating: 4.4,
    reviews: 22,
  },
  {
    id: 'd2',
    name: 'Insulated Water Bottle',
    slug: 'water-bottle',
    price: 3800,
    category: 'Drinkware',
    image: PRODUCT_IMAGES.bottle,
    description: '750ml stainless steel - keeps drinks cold for 24hrs',
    featured: true,
    inStock: true,
    rating: 4.9,
    reviews: 35,
    colors: [
      { name: 'Black', hex: '#1a1a1a' },
      { name: 'Red', hex: '#dc2626' },
      { name: 'White', hex: '#ffffff' },
    ],
  },
  // Accessories
  {
    id: 'a1',
    name: 'Sticker Pack',
    slug: 'sticker-pack',
    price: 1500,
    category: 'Accessories',
    image: PRODUCT_IMAGES.sticker,
    description: 'Set of 5 vinyl stickers',
    featured: false,
    inStock: true,
    rating: 4.8,
    reviews: 45,
  },
  {
    id: 'a2',
    name: 'Metal Keychain',
    slug: 'metal-keychain',
    price: 1800,
    category: 'Accessories',
    image: PRODUCT_IMAGES.keychain,
    description: 'Die-cast metal keychain with track logo',
    featured: false,
    inStock: true,
    rating: 4.5,
    reviews: 18,
  },
  {
    id: 'a3',
    name: 'Pit Crew Lanyard',
    slug: 'pit-lanyard',
    price: 1200,
    category: 'Accessories',
    image: PRODUCT_IMAGES.lanyard,
    description: 'Detachable clip with card holder',
    featured: false,
    inStock: true,
    rating: 4.6,
    reviews: 29,
  },
  {
    id: 'a4',
    name: 'Circuit Poster',
    slug: 'circuit-poster',
    price: 2500,
    category: 'Accessories',
    image: PRODUCT_IMAGES.poster,
    description: 'A2 size track layout poster - perfect for your garage',
    featured: false,
    inStock: true,
    rating: 4.7,
    reviews: 12,
  },
];

const categories = ['All', 'Apparel', 'Headwear', 'Towels', 'Flags', 'Drinkware', 'Accessories'];

interface ProductModalProps {
  product: Product;
  onClose: () => void;
  onAddToCart: (product: Product, size?: string, color?: string, quantity?: number) => void;
}

function ProductModal({ product, onClose, onAddToCart }: ProductModalProps) {
  const [selectedSize, setSelectedSize] = useState(product.sizes?.[0] || '');
  const [selectedColor, setSelectedColor] = useState(product.colors?.[0]?.name || '');
  const [quantity, setQuantity] = useState(1);

  const handleAdd = () => {
    onAddToCart(product, selectedSize, selectedColor, quantity);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 z-10"
        >
          <X size={24} />
        </button>

        <div className="aspect-square">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
        </div>

        <div className="p-6">
          <p className="text-sm text-red-600 font-medium uppercase tracking-wide mb-1">
            {product.category}
          </p>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{product.name}</h2>
          <p className="text-gray-600 mb-4">{product.description}</p>

          <div className="flex items-center gap-2 mb-6">
            <div className="flex items-center">
              <Star size={16} className="text-yellow-400 fill-yellow-400" />
              <span className="ml-1 font-medium">{product.rating}</span>
            </div>
            <span className="text-gray-400">•</span>
            <span className="text-gray-500">{product.reviews} reviews</span>
          </div>

          {/* Size Selection */}
          {product.sizes && product.sizes.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Size</label>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                      selectedSize === size
                        ? 'border-red-600 bg-red-50 text-red-600'
                        : 'border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Color Selection */}
          {product.colors && product.colors.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color: {selectedColor}
              </label>
              <div className="flex gap-2">
                {product.colors.map((color) => (
                  <button
                    key={color.name}
                    onClick={() => setSelectedColor(color.name)}
                    className={`w-10 h-10 rounded-full border-2 transition-all ${
                      selectedColor === color.name
                        ? 'border-red-600 ring-2 ring-red-200'
                        : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color.hex }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Quantity */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Quantity</label>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                <Minus size={18} />
              </button>
              <span className="w-12 text-center font-medium text-lg">{quantity}</span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                <Plus size={18} />
              </button>
            </div>
          </div>

          {/* Price & Add to Cart */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div>
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatPrice(product.price * quantity)}
              </p>
            </div>
            <button
              onClick={handleAdd}
              disabled={!product.inStock}
              className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ShoppingCart size={20} />
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ShopPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('featured');
  const [addedItems, setAddedItems] = useState<Set<string>>(new Set());
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const { addItem, totalItems } = useCartStore();

  const handleAddToCart = (
    product: Product,
    size?: string,
    color?: string,
    quantity: number = 1
  ) => {
    addItem({
      productId: product.id,
      name: product.name,
      price: product.price,
      quantity,
      type: 'merchandise',
      imageUrl: product.image,
      size,
      color,
    });
    setAddedItems((prev) => new Set(prev).add(product.id));
    setTimeout(() => {
      setAddedItems((prev) => {
        const next = new Set(prev);
        next.delete(product.id);
        return next;
      });
    }, 2000);
  };

  const handleQuickAdd = (product: Product) => {
    if (product.sizes || product.colors) {
      setSelectedProduct(product);
    } else {
      handleAddToCart(product);
    }
  };

  const filteredProducts = products
    .filter((p) => selectedCategory === 'All' || p.category === selectedCategory)
    .sort((a, b) => {
      if (sortBy === 'featured') return b.featured ? 1 : -1;
      if (sortBy === 'price-low') return a.price - b.price;
      if (sortBy === 'price-high') return b.price - a.price;
      if (sortBy === 'rating') return b.rating - a.rating;
      return 0;
    });

  const featuredProducts = products.filter((p) => p.featured).slice(0, 4);

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Merchandise Shop</h1>
            <p className="text-gray-600 max-w-xl">
              Official DriveIT NQ apparel and accessories. Show your support for North Queensland
              motorsport.
            </p>
          </div>
          <Link
            to="/cart"
            className="relative flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
          >
            <ShoppingCart size={20} />
            <span className="font-medium">Cart</span>
            {totalItems() > 0 && (
              <span className="absolute -top-2 -right-2 w-6 h-6 bg-white text-red-600 rounded-full flex items-center justify-center text-sm font-bold">
                {totalItems()}
              </span>
            )}
          </Link>
        </div>

        {/* Featured Products */}
        <section className="mb-12">
          <div className="bg-gradient-to-r from-gray-900 to-red-900 rounded-2xl p-8 text-white">
            <h2 className="text-2xl font-bold mb-6">Featured Products</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {featuredProducts.map((product) => (
                <div
                  key={product.id}
                  onClick={() => setSelectedProduct(product)}
                  className="bg-white/10 backdrop-blur rounded-xl overflow-hidden cursor-pointer hover:bg-white/20 transition-colors"
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-40 object-cover"
                  />
                  <div className="p-4">
                    <p className="text-xs text-red-300 uppercase tracking-wide mb-1">
                      {product.category}
                    </p>
                    <h3 className="font-semibold truncate">{product.name}</h3>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-lg font-bold">{formatPrice(product.price)}</span>
                      <div className="flex items-center text-sm">
                        <Star size={14} className="text-yellow-400 fill-yellow-400 mr-1" />
                        {product.rating}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Filters */}
        <div id="products" className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            <Filter size={18} className="text-gray-400 flex-shrink-0" />
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === cat
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-red-500 focus:border-red-500"
          >
            <option value="featured">Featured</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="rating">Top Rated</option>
          </select>
        </div>

        {/* Products Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredProducts.map((product) => (
            <div key={product.id} className="card overflow-hidden group">
              <div
                className="relative aspect-square cursor-pointer"
                onClick={() => setSelectedProduct(product)}
              >
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                {!product.inStock && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="px-4 py-2 bg-white text-gray-900 font-medium rounded-full">
                      Out of Stock
                    </span>
                  </div>
                )}
                {product.featured && product.inStock && (
                  <span className="absolute top-3 left-3 px-2 py-1 bg-red-600 text-white text-xs font-medium rounded-full">
                    Featured
                  </span>
                )}
                {(product.sizes || product.colors) && product.inStock && (
                  <span className="absolute top-3 right-3 px-2 py-1 bg-white/90 text-gray-700 text-xs font-medium rounded-full">
                    <Shirt size={12} className="inline mr-1" />
                    Options
                  </span>
                )}
              </div>

              <div className="p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                  {product.category}
                </p>
                <h3
                  className="font-semibold text-gray-900 mb-1 group-hover:text-red-600 transition-colors cursor-pointer"
                  onClick={() => setSelectedProduct(product)}
                >
                  {product.name}
                </h3>
                <p className="text-sm text-gray-600 mb-2 line-clamp-1">{product.description}</p>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-lg font-bold text-gray-900">
                    {formatPrice(product.price)}
                  </span>
                  <div className="flex items-center text-sm text-gray-500">
                    <Star size={14} className="text-yellow-400 fill-yellow-400 mr-1" />
                    {product.rating}
                  </div>
                </div>
                {product.inStock && (
                  <button
                    onClick={() => handleQuickAdd(product)}
                    disabled={addedItems.has(product.id)}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                      addedItems.has(product.id)
                        ? 'bg-green-600 text-white'
                        : 'bg-red-600 text-white hover:bg-red-700'
                    }`}
                  >
                    {addedItems.has(product.id) ? (
                      <>
                        <Check size={16} />
                        Added!
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        {product.sizes || product.colors ? 'Select Options' : 'Add to Cart'}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Cart CTA */}
        {totalItems() > 0 && (
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
              <ShoppingCart size={24} className="text-gray-400" />
              <div className="text-left">
                <p className="font-medium text-gray-900">
                  {totalItems()} item{totalItems() !== 1 ? 's' : ''} in cart
                </p>
                <p className="text-sm text-gray-600">Free shipping on orders over $100</p>
              </div>
              <Link
                to="/cart"
                className="px-6 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                View Cart
              </Link>
            </div>
          </div>
        )}

        {/* Info */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <div className="text-center p-6">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShoppingCart className="text-red-600" size={24} />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Free Shipping</h3>
            <p className="text-sm text-gray-600">On all orders over $100 Australia-wide</p>
          </div>
          <div className="text-center p-6">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Star className="text-red-600" size={24} />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Quality Guaranteed</h3>
            <p className="text-sm text-gray-600">Premium materials and printing</p>
          </div>
          <div className="text-center p-6">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Filter className="text-red-600" size={24} />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Easy Returns</h3>
            <p className="text-sm text-gray-600">30-day return policy on all items</p>
          </div>
        </div>
      </div>

      {/* Product Modal */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onAddToCart={handleAddToCart}
        />
      )}
    </div>
  );
}
